<script setup lang="ts">
import { computed, onBeforeMount, onMounted, ref } from "vue";
import { useApiClient } from "@/api/client";
import TableBucketValues from "@/modules/dimension_values/components/TableBucketValues.vue";
import useDialog from "@/ui/composables/useDialog";
import { toastApiResult } from "@/toaster";
import { t } from "@/plugins/i18n";
import BucketCreation from "@/modules/dimension_values/components/BucketCreation.vue";
import BucketPutDTO = App.Data.BucketPutDTO;
import DimensionBucketDO = App.Data.DimensionBucketDO;
import router from "@/router/router";
import MainBreadcrumbs from "@/ui/components/header/MainBreadcrumbs.vue";
import { onBeforeRouteLeave } from "vue-router";
import { toRawDeep } from "@/Utils/to-raw-deep";
import isEqual from "lodash/isEqual";
import { unsavedChangesDialog } from "@/ui/composables/useUnsavedChangesDialog";

type DimensionDO = App.Data.DimensionDO;
type BucketValuePutDTO = App.Data.BucketValuePutDTO;

// ============================================================================

const client = useApiClient("dimensions");
const dialog = useDialog();

// Dimensions
// ↳ Dimension (WHERE id=selectedDimensionId)
//   ↳ Bucket #1
//     ↳ Value #1 { start_value: -∞, label: "< 10" }
//     ↳ Value #2 { start_value: 10, label: "10 - 20" }
//     ↳ Value #3 { start_value: 20, label: "20 - 30" }
//     ↳ Value #4 { start_value: 30, label: "> 30" }
//   ↳ Bucket #2
//     ↳ Value #1 { ... }
//     ↳ Value #2 { ... }

const dimensionId = parseInt(router.currentRoute.value.params.id as string);
const dimensionNameClean = router.currentRoute.value.params.name_clean;
const dimension = ref<DimensionDO | null>(null);

const isCreatingBucket = ref(false);
const buckets = ref<BucketPutDTO[]>([]);
const savedBuckets = ref<BucketPutDTO[]>([]);
const loading = ref(false);
const isFormValid = ref(true);

const breadcrumbs = [
	{
		title: "Dimension Values",
		disabled: false,
		to: "/dimension-values",
	},
	{
		title: t(`properties.${dimensionNameClean}.title`, "Bucket Values"),
		disabled: true,
		to: "/dimension-values",
	},
];

async function loadPage() {
	loading.value = true;
	const response = await client.getBuckets(dimensionId);
	if (!response.isOk()) {
		await dialog.alert({
			title: "Could not get bucket values",
			message: "",
		});
		loading.value = false;
		return;
	}

	dimension.value = response.value.dimension;
	const fetchedBuckets = response.value.buckets;

	buckets.value = (
		await Promise.all(
			fetchedBuckets.map(async (bucket) => {
				const response = await client.getBucketValues(bucket.id);

				if (!response.isOk()) return null;

				return {
					bucket,
					values: response.value.map((value): BucketValuePutDTO => {
						return {
							dimension_value_id: value.id,
							label: value.label,
							sort_order: value.sort_order,
							hex_color_value: value.hex_color_value,
							start_value: value.start_value,
						};
					}),
					deletions: [],
				};
			})
		)
	).filter((bucket) => bucket !== null);

	savedBuckets.value = structuredClone(toRawDeep(buckets.value));

	loading.value = false;
}

onMounted(async () => loadPage());

async function updateBucket(bucketPutData: BucketPutDTO) {
	loading.value = true;
	await toastApiResult(client.setBucketValues(bucketPutData));
	await loadPage();
	savedBuckets.value = structuredClone(toRawDeep(buckets.value));
	loading.value = false;
}

async function setBucketAsDefault(bucket: BucketPutDTO) {
	const response = await toastApiResult(
		client.setBucketAsDefault(bucket.bucket.id)
	);

	if (!response.isOk()) return;

	for (let b of buckets.value) {
		if (b.bucket === bucket.bucket) {
			b.bucket.is_default = true;
		} else if (b.bucket.is_default) {
			b.bucket.is_default = false;
		}
	}
}

async function deleteBucket(bucket: DimensionBucketDO) {
	const confirm = await dialog.confirm({
		title: "Delete Bucket",
		message: `Are you sure you want to delete this bucket?`,
		isDelete: true,
	});
	if (!confirm) return;

	loading.value = true;
	const response = await toastApiResult(client.deleteBucket(bucket.id));
	if (response.isOk()) {
		if (bucket.is_default) {
			// There's an edge case where a new default needs to be selected if the current default bucket is deleted
			// We just reload the page to fetch that from the backend
			await loadPage();
		} else {
			buckets.value = buckets.value.filter((b) => b.bucket !== bucket);
		}
		savedBuckets.value = structuredClone(toRawDeep(buckets.value));
	}
	loading.value = false;
}

const isDirty = computed(() => {
	return !isEqual(buckets.value, savedBuckets.value);
});

onBeforeRouteLeave((to, from, next) => {
	unsavedChangesDialog("bucket", isDirty.value, next);
});

const beforeUnload = (event: BeforeUnloadEvent) => {
	if (!isDirty.value) {
		return;
	}
	event.preventDefault();
	event.returnValue = true;
};

onBeforeMount(() => window.addEventListener("beforeunload", beforeUnload));
</script>

<template>
	<main-breadcrumbs :breadcrumbs="breadcrumbs" />

	<bucket-creation
		v-model:is-open="isCreatingBucket"
		class="mb-6"
		:dimension="dimension"
		:buckets-count="buckets.length"
		@reload="loadPage"
	/>

	<div class="d-flex flex-row justify-end ga-4 mb-4">
		<v-card v-if="!buckets.length" class="pa-3 w-100">
			This dimension doesn't have any buckets yet...
		</v-card>
		<v-btn
			height="48"
			elevation="0"
			prepend-icon="mdi-plus"
			@click="isCreatingBucket = true"
		>
			New Bucket
		</v-btn>
	</div>

	<v-card
		v-for="bucket in buckets"
		:key="bucket.bucket.id"
		class="mb-6 pa-4 pb-2"
	>
		<v-form v-model="isFormValid">
			<table-bucket-values
				v-model:loading="loading"
				v-model:bucket="bucket as BucketPutDTO"
				:dimension="dimension"
				:is-form-valid="isFormValid"
				@save="updateBucket(bucket)"
				@set-as-default="setBucketAsDefault(bucket)"
				@delete="deleteBucket(bucket.bucket)"
			/>
		</v-form>
	</v-card>
</template>
